<template>
    <SmartButton v-model="showPopup" data-tooltip="Indikator auswählen"
        class="v-atlas-raumplanung-request-options__attribute-selector-button" boolean>
        {{ getAttributeLabel() }}
    </SmartButton>

    <Popup v-model:show="showPopup" title="Indikator auswählen" class="x-w-full">

        <div class="v-atlas-raumplanung-attribute-selector">
            <div class="v-atlas-raumplanung-attribute-selector__filter">
                <SmartTextInput v-model="filterText" type="text" placeholder="Filtern nach Bezeichnung …" />
            </div>

            <div class="v-atlas-raumplanung-attribute-selector__tree">
                <TreeNode v-if="tocTree" :tree="tocTree" :show-root="false" :filter-func="filterFunc"
                    v-model:state="treeState">
                    <template #body="{ expanded, node, toggle }">
                        <button class="v-atlas-raumplanung-attribute-selector__node-row" :class="{
                            'v-atlas-raumplanung-attribute-selector__node-row--leaf': isLeaf(node),
                            'v-atlas-raumplanung-attribute-selector__node-row--parent': !isLeaf(node),
                            'v-atlas-raumplanung-attribute-selector__node-row--expanded': expanded
                        }" @click="isLeaf(node) ? onLeafClick(node) : toggle()">
                            <span v-if="!isLeaf(node)" class="v-atlas-raumplanung-attribute-selector__expand-icon">
                                {{ expanded ? '▾' : '▸' }}
                            </span>
                            <span class="v-atlas-raumplanung-attribute-selector__node-label">
                                {{ node.label }}
                            </span>
                        </button>
                    </template>
                </TreeNode>

                <div v-if="tocTree && !hasVisibleNodes" class="v-atlas-raumplanung-attribute-selector__empty">
                    Keine Indikatoren gefunden.
                </div>
            </div>
        </div>
    </Popup>
</template>

<script setup lang="ts">


/*

This component provides an interface to select an attribute for visualization in the Raumplanung module.
It is intended to be used as a popup that appears when the user clicks on the "Indikator auswählen" button
in the RaumplanungMain component.

The component loads a hierarchical tree of categories, sub-categories, and indicator leaves from the Atlas
back-end's "<Raumplanung-Service>/toc_tree" endpoint. It renders a collapsible tree using the TreeNode
component from gertrud. Clicking a leaf node selects the corresponding indicator.

*/


import { computed, Ref, ref, watch } from 'vue';
import { Popup, SmartButton, useSmartFetch, TreeNode, SmartTextInput } from 'gertrud';
import { AttributeSelectorRequestOptionsItem } from 'atlas-shared/dist/RequestOptions';


/**
 * A node in the table-of-contents tree served by the Raumplanung service's
 * `/toc_tree` endpoint.
 */
interface TocTreeNode {
    label: string
    children?: TocTreeNode[]
    andromedaAttrName?: string
}

const props = defineProps<{
    option: AttributeSelectorRequestOptionsItem;
    modelValue: string;

}>();


const emit = defineEmits<{
    'update:modelValue': [value: string];
}>();


const showPopup = ref(false);
const filterText = ref("")
const treeState = ref<any>({})
const tocTreeUrl = ref(`${props.option.serviceBaseUrl}/toc_tree`);





const { data: tocTreeResponse, isLoading, error } = useSmartFetch<any>(tocTreeUrl)

const tocTree = computed(() => {

    return tocTreeResponse.value?.tocTree
})


function getAttributeLabel(): string {

    return tocTreeResponse.value?.datasets?.[props.modelValue]?.label || "Kein Indikator ausgewählt"

}




/**
 * Filter function passed to TreeNode. Returns undefined when no filter is active
 * (show all nodes), or a boolean indicating whether the node matches the filter text.
 */
const filterFunc = computed(() => {
    const needle = filterText.value.trim().toLowerCase()
    if (!needle) return undefined
    return (node: TocTreeNode) => node.label.toLowerCase().includes(needle)
})

/**
 * Whether any nodes are visible after filtering. Used to show an empty-state message.
 */
const hasVisibleNodes = computed(() => {
    if (!tocTree.value) return false
    if (!filterText.value.trim()) return true
    return nodeOrChildMatches(tocTree.value, filterText.value.trim().toLowerCase())
})

function nodeOrChildMatches(node: TocTreeNode, needle: string): boolean {
    if (node.label.toLowerCase().includes(needle)) return true
    if (node.children) {
        return node.children.some(child => nodeOrChildMatches(child, needle))
    }
    return false
}




function isLeaf(node: TocTreeNode): boolean {
    return !node.children || node.children.length === 0
}



//#region Event handlers

function onLeafClick(node: TocTreeNode) {
    if (!node.andromedaAttrName) return



    emit("update:modelValue", node.andromedaAttrName)

    showPopup.value = false
}

//#endregion
</script>